import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { feedAPI } from '@/lib/api';
import { Smile, Image as ImageIcon, Link as LinkIcon, X, Loader2 } from 'lucide-react';

const MOODS = [
  { value: 'calmo', label: 'Calmo 😌', emoji: '😌' },
  { value: 'ansioso', label: 'Ansioso 😰', emoji: '😰' },
  { value: 'motivado', label: 'Motivado 💪', emoji: '💪' },
  { value: 'grato', label: 'Grato 🙏', emoji: '🙏' },
  { value: 'triste', label: 'Triste 😢', emoji: '😢' },
];

const MAX_CONTENT_LENGTH = 1000;
const MAX_IMAGES = 4;
const MAX_IMAGE_SIZE_MB = 5;

export function FeedComposer({ onPostCreated, groupId = null }) {
  const [content, setContent] = useState('');
  const [mood, setMood] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [images, setImages] = useState([]);
  const [showMoodSelector, setShowMoodSelector] = useState(false);
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    
    if (images.length + files.length > MAX_IMAGES) {
      toast.error(`Você pode adicionar no máximo ${MAX_IMAGES} imagens`);
      return;
    }

    // Validate file size and type
    const validFiles = [];
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} não é uma imagem`);
        continue;
      }
      
      if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
        toast.error(`${file.name} é muito grande (máx ${MAX_IMAGE_SIZE_MB}MB)`);
        continue;
      }
      
      validFiles.push(file);
    }

    // Create preview URLs
    const newImages = validFiles.map(file => ({
      file,
      preview: URL.createObjectURL(file),
    }));

    setImages([...images, ...newImages]);
  };

  const removeImage = (index) => {
    const newImages = [...images];
    URL.revokeObjectURL(newImages[index].preview);
    newImages.splice(index, 1);
    setImages(newImages);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!content.trim()) {
      toast.error('Escreva algo antes de publicar');
      return;
    }

    if (content.length > MAX_CONTENT_LENGTH) {
      toast.error(`O texto não pode ter mais de ${MAX_CONTENT_LENGTH} caracteres`);
      return;
    }

    setIsSubmitting(true);

    try {
      let newPost;

      if (images.length > 0) {
        // Use multipart/form-data for posts with images
        const formData = new FormData();
        formData.append('content', content.trim());
        if (mood) formData.append('mood', mood);
        if (linkUrl.trim()) formData.append('link_url', linkUrl.trim());
        if (groupId) formData.append('group_id', groupId);
        
        // Append all images
        images.forEach((img) => {
          formData.append('images', img.file);
        });

        const response = await feedAPI.createPostWithImages(formData);
        newPost = response.data;
      } else {
        // Use JSON for posts without images
        const postData = {
          content: content.trim(),
          mood: mood || null,
          link_url: linkUrl.trim() || null,
          media_urls: null,
        };

        if (groupId) {
          postData.group_id = groupId;
        }

        const response = await feedAPI.createPost(postData);
        newPost = response.data;
      }

      // Notify parent component
      if (onPostCreated) {
        onPostCreated(newPost);
      }

      // Reset form
      setContent('');
      setMood('');
      setLinkUrl('');
      // Clean up image previews
      images.forEach(img => URL.revokeObjectURL(img.preview));
      setImages([]);
      setShowMoodSelector(false);
      setShowLinkInput(false);

      toast.success('Post publicado com sucesso!');
    } catch (error) {
      console.error('Failed to create post:', error);
      toast.error(error.response?.data?.detail || 'Erro ao publicar post');
    } finally {
      setIsSubmitting(false);
    }
  };

  const remainingChars = MAX_CONTENT_LENGTH - content.length;
  const isOverLimit = remainingChars < 0;

  return (
    <Card className="p-4 mb-6" data-testid="feed-composer">
      <form onSubmit={handleSubmit}>
        <div className="space-y-4">
          {/* Textarea */}
          <div>
            <Textarea
              data-testid="feed-composer-textarea"
              placeholder="Como você está se sentindo hoje?"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className={`min-h-[100px] resize-none ${isOverLimit ? 'border-red-500' : ''}`}
              aria-label="Conteúdo do post"
              aria-describedby="char-counter"
            />
            <div
              id="char-counter"
              className={`text-sm text-right mt-1 ${
                isOverLimit ? 'text-red-500' : remainingChars < 50 ? 'text-yellow-600' : 'text-muted-foreground'
              }`}
            >
              {remainingChars} caracteres restantes
            </div>
          </div>

          {/* Mood Selector */}
          {showMoodSelector && (
            <div>
              <Label htmlFor="mood-select">Como você está se sentindo?</Label>
              <Select value={mood} onValueChange={setMood}>
                <SelectTrigger id="mood-select" data-testid="mood-selector" aria-label="Selecionar humor">
                  <SelectValue placeholder="Selecione um humor" />
                </SelectTrigger>
                <SelectContent>
                  {MOODS.map((m) => (
                    <SelectItem key={m.value} value={m.value}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Link Input */}
          {showLinkInput && (
            <div>
              <Label htmlFor="link-input">Adicionar link</Label>
              <Input
                id="link-input"
                data-testid="link-input"
                type="url"
                placeholder="https://..."
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                aria-label="URL do link"
              />
            </div>
          )}

          {/* Image Previews */}
          {images.length > 0 && (
            <div className="grid grid-cols-2 gap-2" data-testid="image-previews">
              {images.map((img, index) => (
                <div key={index} className="relative aspect-square">
                  <img
                    src={img.preview}
                    alt={`Preview ${index + 1}`}
                    className="w-full h-full object-cover rounded-lg"
                  />
                  <Button
                    type="button"
                    size="icon"
                    variant="destructive"
                    className="absolute top-2 right-2 h-6 w-6"
                    onClick={() => removeImage(index)}
                    aria-label={`Remover imagem ${index + 1}`}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t">
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowMoodSelector(!showMoodSelector)}
                data-testid="toggle-mood-button"
                aria-label="Adicionar humor"
              >
                <Smile className="h-4 w-4 mr-1" />
                Humor
              </Button>

              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => document.getElementById('image-upload').click()}
                disabled={images.length >= MAX_IMAGES}
                data-testid="add-images-button"
                aria-label="Adicionar imagens"
              >
                <ImageIcon className="h-4 w-4 mr-1" />
                Imagens ({images.length}/{MAX_IMAGES})
              </Button>

              <input
                id="image-upload"
                type="file"
                accept="image/jpeg,image/png,image/jpg,image/webp"
                multiple
                onChange={handleImageUpload}
                className="hidden"
                aria-label="Upload de imagens"
              />

              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowLinkInput(!showLinkInput)}
                data-testid="toggle-link-button"
                aria-label="Adicionar link"
              >
                <LinkIcon className="h-4 w-4 mr-1" />
                Link
              </Button>
            </div>

            <Button
              type="submit"
              disabled={!content.trim() || isOverLimit || isSubmitting}
              data-testid="submit-post-button"
              aria-label="Publicar post"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Publicando...
                </>
              ) : (
                'Publicar'
              )}
            </Button>
          </div>
        </div>
      </form>
    </Card>
  );
}
